import { useEffect, useId, useRef, useState } from "react";
import MARK from "../../assets/Mas_logo_loader.png";

// The logo's aspect, so the fill tracks its height.
const W = 100;
const H = 26.3;

// The MAS logo filling with liquid: a faded copy underneath, the full-colour
// logo on top masked to a wave whose level follows `progress` (0–1). With
// `progress` null it fills and drains on a loop (unknown duration).
export default function LogoLoader({ progress = null, size = 170 }) {
  const maskId = `logo-fill-${useId().replace(/:/g, "")}`;
  const indeterminate = progress == null;

  // Ease the level toward the target so jumpy progress events still read
  // as a smooth rise.
  const [level, setLevel] = useState(0);
  const target = indeterminate ? null : Math.max(0.08, Math.min(1, progress));
  const levelRef = useRef(0);
  useEffect(() => {
    if (target == null) return undefined;
    let frame;
    const step = () => {
      const next = levelRef.current + (target - levelRef.current) * 0.12;
      levelRef.current = Math.abs(target - next) < 0.002 ? target : next;
      setLevel(levelRef.current);
      if (levelRef.current !== target) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  // Surface y of the liquid; a little headroom so the wave crests show at 100%.
  const surface = H + 4 - level * (H + 8);

  return (
    <svg
      className={`logo-loader ${indeterminate ? "indeterminate" : ""}`}
      width={size}
      height={(size * H) / W}
      viewBox={`0 0 ${W} ${H}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(progress * 100)}
    >
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
          <g
            className="logo-loader-level"
            transform={indeterminate ? undefined : `translate(0 ${surface})`}
          >
            {/* Two wave periods (50 units each) slide left by one period, seamlessly. */}
            <path
              fill="#fff"
              d="M0 0 Q12.5 -4 25 0 T50 0 T75 0 T100 0 T125 0 T150 0 V200 H0 Z"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                from="0 0"
                to="-50 0"
                dur="1.4s"
                repeatCount="indefinite"
              />
            </path>
            {indeterminate && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 ${H + 4};0 -4;0 ${H + 4}`}
                dur="2.6s"
                repeatCount="indefinite"
              />
            )}
          </g>
        </mask>
      </defs>
      <image href={MARK} width={W} height={H} className="logo-loader-ghost" />
      <image href={MARK} width={W} height={H} mask={`url(#${maskId})`} />
    </svg>
  );
}
